import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import {
    MediaPersonnel,
    MediaPersonnelFilters,
    MediaPersonnelType,
} from "../types/media";
import { useLeagueSeasonStore } from "@/state/useLeagueSeasonStore";
import { processBatch } from "../utils/batchProcessor";
import { getPersonnelName } from "../services/mediaService";
import { PERSONNEL_MUTATION_INVALIDATES } from "./mediaInvalidation";
import {
    messageFromInvokeError,
    messageFromInvokeBody,
    mediaErrorFromBody,
    mediaErrorFromInvoke,
} from "./mediaInvokeError";
import type {
    ProfileReviewAction,
    ProfileReviewResult,
} from "./mediaProfileReviewLogic";


interface UseMediaPersonnelOptions {
    filters?: MediaPersonnelFilters;
    enabled?: boolean;
}

interface MediaPersonnelListResult {
    data: MediaPersonnel[];
    total: number;
}

/**
 * Hook to fetch media personnel with filters
 * Scoped by the global league selection
 */
export function useMediaPersonnel(options: UseMediaPersonnelOptions = {}) {
    const { filters = {}, enabled = true } = options;
    const currentLeagueId = useLeagueSeasonStore((state) => state.currentLeagueId);

    return useQuery<MediaPersonnelListResult>({
        queryKey: ["media-personnel", currentLeagueId, filters],
        queryFn: async () => {
            let query = supabase
                .from("media_personnel")
                .select(
                    `
          *,
          profile:profiles!profile_id (
            id,
            full_name,
            email,
            phone,
            avatar_url
          ),
          organization:media_organizations!organization_id (
            id,
            name,
            name_ar,
            org_type,
            account_kind,
            logo_url,
            is_approved,
            deleted_at
          ),
          affiliated_team:teams!affiliated_team_id (
            id,
            name,
            name_ar
          )
        `,
                    { count: "exact" }
                )
                // Deleted journalists leave the league's lists (deleting an
                // organisme deletes its journalists too). `deleted_at` comes
                // with `*`.
                .is("deleted_at", null)
                .order("created_at", { ascending: false });

            // Apply filters
            if (filters.isVerified !== undefined && filters.isVerified !== null) {
                query = query.eq("is_verified", filters.isVerified);
            }

            if (filters.mediaType) {
                query = query.eq("media_type", filters.mediaType);
            }

            if (filters.organizationId) {
                query = query.eq("organization_id", filters.organizationId);
            }

            if (filters.searchQuery) {
                // Search press card + denormalized bio (imported personnel have no profile)
                const q = filters.searchQuery;
                query = query.or(
                    `press_card_number.ilike.%${q}%,first_name.ilike.%${q}%,last_name.ilike.%${q}%,first_name_ar.ilike.%${q}%,last_name_ar.ilike.%${q}%,email.ilike.%${q}%`
                );
            }

            const { data, error, count } = await query;

            if (error) {
                throw new Error(error.message);
            }

            return {
                data: (data || []) as MediaPersonnel[],
                total: count || 0,
            };
        },
        enabled: enabled && !!currentLeagueId,
        staleTime: 30000, // 30 seconds
    });
}

export interface ReviewMediaProfilePayload {
    personnelId: string;
    action: ProfileReviewAction;
    reason?: string;
}

/**
 * Accept / reject a journalist profile through the `review-media-profile` edge
 * function. Accepting a profile without a login also creates the login and
 * e-mails the credentials (not on a re-review); the acceptance stands even
 * when that part fails — read `account` in the result, never assume.
 *
 * The profile is never written direct-client any more: the mobile guard
 * mirrors is_verified from lirf_status, so the former is_verified UPDATE
 * would be silently undone.
 */
export function useReviewMediaProfile() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            personnelId,
            action,
            reason,
        }: ReviewMediaProfilePayload): Promise<ProfileReviewResult> => {
            const { data, error } = await supabase.functions.invoke(
                "review-media-profile",
                {
                    body: {
                        personnel_id: personnelId,
                        action,
                        ...(reason?.trim() ? { reason: reason.trim() } : {}),
                    },
                }
            );
            if (error) throw await mediaErrorFromInvoke(error);
            const bodyError = mediaErrorFromBody(data);
            if (bodyError) throw bodyError;
            return data as ProfileReviewResult;
        },
        // Settled, not success: a 409 means another reviewer decided first,
        // and the list must show that decision.
        onSettled: () => {
            PERSONNEL_MUTATION_INVALIDATES.forEach((k) =>
                queryClient.invalidateQueries({ queryKey: [k] })
            );
        },
    });
}

/**
 * Mutation to set a journalist's participation authorization
 * (`participation_suspended`). This is distinct from is_verified (accept/reject):
 * the federation can suspend participation of an otherwise-verified journalist.
 * Runs through the admin (privileged) path, so the federation-fields DB trigger
 * allows the write.
 */
export function useSetPersonnelParticipation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            personnelId,
            suspended,
        }: {
            personnelId: string;
            suspended: boolean;
        }) => {
            const { data, error } = await supabase
                .from("media_personnel")
                .update({ participation_suspended: suspended })
                .eq("id", personnelId)
                .select()
                .single();

            if (error) {
                throw new Error(error.message);
            }

            return data;
        },
        onSuccess: () => {
            // Suspending participation changes what request cards may show
            // (see hooks/mediaInvalidation.ts).
            PERSONNEL_MUTATION_INVALIDATES.forEach((k) =>
                queryClient.invalidateQueries({ queryKey: [k] })
            );
        },
    });
}

export interface ProvisionJournalistLoginResult {
    email: string;
    password: string;
    user_id: string;
    reset?: boolean;
    email_sent?: boolean;
    email_error?: string;
}

/**
 * Provision (or reset) a single journalist's mobile login via the
 * `provision-journalist-account` edge function, emailing them Arabic
 * credentials unless sendEmail === false. Returns the credentials once.
 */
export function useProvisionJournalistLogin() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            personnelId,
            email,
            password,
            sendEmail,
        }: {
            personnelId: string;
            email?: string;
            password?: string;
            sendEmail?: boolean;
        }): Promise<ProvisionJournalistLoginResult> => {
            const { data, error } = await supabase.functions.invoke(
                "provision-journalist-account",
                {
                    body: {
                        personnel_id: personnelId,
                        ...(email ? { email } : {}),
                        ...(password ? { password } : {}),
                        ...(sendEmail === false ? { send_email: false } : {}),
                    },
                }
            );
            // Coded errors (EMAIL_IN_USE, NO_EMAIL, …) so the UI can localize them.
            if (error) throw await mediaErrorFromInvoke(error);
            const bodyError = mediaErrorFromBody(data);
            if (bodyError) throw bodyError;
            return data as ProvisionJournalistLoginResult;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["media-personnel"] });
        },
    });
}

export interface BulkOnboardItemResult {
    personnelId: string;
    name: string;
    ok: boolean;
    email_sent?: boolean;
    error?: string;
}

/**
 * Onboard many journalists at once (concurrency-limited) via the same edge
 * function. The caller passes the ALREADY-ELIGIBLE list (see
 * partitionOnboardingCandidates). Never throws per-item — every journalist
 * ends up in `succeeded` or `failed`. Invalidates the list once at the end.
 */
export function useBulkOnboardJournalists() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            personnel,
            onProgress,
        }: {
            personnel: MediaPersonnel[];
            onProgress?: (done: number, total: number) => void;
        }): Promise<{ succeeded: BulkOnboardItemResult[]; failed: BulkOnboardItemResult[] }> => {
            const outcome = await processBatch<MediaPersonnel, BulkOnboardItemResult>({
                items: personnel,
                concurrency: 5,
                onProgress,
                processor: async (p) => {
                    const name = getPersonnelName(p, true) || p.email || p.id;
                    const { data, error } = await supabase.functions.invoke(
                        "provision-journalist-account",
                        { body: { personnel_id: p.id } }
                    );
                    if (error) throw new Error(await messageFromInvokeError(error));
                    const bodyError = messageFromInvokeBody(data);
                    if (bodyError) throw new Error(bodyError);
                    return {
                        personnelId: p.id,
                        name,
                        ok: true,
                        email_sent: data?.email_sent,
                    };
                },
            });

            const succeeded = outcome.results;
            const failed: BulkOnboardItemResult[] = outcome.errors.map((e) => {
                const p = personnel[e.index];
                return {
                    personnelId: p.id,
                    name: getPersonnelName(p, true) || p.email || p.id,
                    ok: false,
                    error: e.error instanceof Error ? e.error.message : String(e.error),
                };
            });
            return { succeeded, failed };
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["media-personnel"] });
        },
    });
}

// NOTE: media-organization data access lives in hooks/useMediaOrganizations.ts
// (query + create/update/agrément/participation/provision mutations).
